import React, {useEffect, useId, useMemo, useRef} from "react";
import {NightVision} from "night-vision";

function timestamp(value) {
    const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizedCandles(trade) {
    return [...(trade.candles || [])]
        .map((candle) => ({
            time: timestamp(candle.timestamp),
            open: Number(candle.open),
            high: Number(candle.high),
            low: Number(candle.low),
            close: Number(candle.close),
        }))
        .filter((candle) => candle.time != null && [candle.open, candle.high, candle.low, candle.close].every((value) => Number.isFinite(value) && value > 0))
        .sort((left, right) => left.time - right.time);
}

function normalizedFills(fills) {
    return fills
        .map((fill) => ({
            time: timestamp(fill.timestamp),
            price: Number(fill.price),
            quantity: Number(fill.quantity),
            side: String(fill.side || "").toLowerCase(),
            label: `${fill.side || "Fill"} ${Math.abs(Number(fill.quantity) || 0).toLocaleString()} @ ${Number(fill.price).toFixed(2)}`,
        }))
        .filter((fill) => fill.time != null && Number.isFinite(fill.price) && fill.price > 0 && Number.isFinite(fill.quantity) && (fill.side === "buy" || fill.side === "sell"));
}

function priceLines(candles, fills, trade) {
    const times = candles.map((candle) => candle.time).concat(fills.map((fill) => fill.time));
    if (!times.length) return [];
    const start = Math.min(...times);
    const end = Math.max(...times, start + 1);
    return [
        {id: "stop-loss", label: "SL", value: trade.plannedStopLoss, color: "#fb7185"},
        {id: "take-profit", label: "TP", value: trade.plannedTakeProfit, color: "#34d399"},
    ]
        .filter((line) => Number.isFinite(Number(line.value)) && Number(line.value) > 0)
        .map((line) => ({...line, value: Number(line.value), data: [[start, Number(line.value)], [end, Number(line.value)]]}));
}

export function TradeCandlestickChart({trade, fills = []}) {
    const containerId = `trade-chart-${useId().replace(/:/g, "")}`;
    const chartRef = useRef(null);
    const signature = useMemo(() => JSON.stringify({
        symbol: trade.symbol,
        candles: trade.candles || [],
        fills,
        stop: trade.plannedStopLoss,
        target: trade.plannedTakeProfit,
    }), [trade, fills]);
    const candles = useMemo(() => normalizedCandles(trade), [signature]);
    const normalized = useMemo(() => normalizedFills(fills), [signature]);
    const lines = useMemo(() => priceLines(candles, normalized, trade), [candles, normalized, signature]);

    useEffect(() => {
        if (!candles.length) return undefined;
        const precision = candles.some((candle) => candle.close < 1) ? 4 : 2;
        const chartData = {
            panes: [{overlays: [
                {
                    name: `${trade.symbol} candles`,
                    type: "Candles",
                    main: true,
                    data: candles.map((candle) => [candle.time, candle.open, candle.high, candle.low, candle.close, 0]),
                    settings: {precision},
                    props: {currencySymbol: ""},
                },
                ...lines.map((line) => ({
                    name: line.label,
                    type: "Spline",
                    data: line.data,
                    settings: {precision},
                    props: {color: line.color, lineWidth: 1.5},
                })),
                {
                    name: "Execution fills",
                    type: "Trades",
                    data: normalized.map((fill) => [fill.time, fill.side === "buy" ? 1 : -1, Number(fill.price.toFixed(4)), fill.label]),
                    settings: {zIndex: 10, precision},
                    props: {
                        markerShape: "triangle",
                        markerOutline: true,
                        outlineWidth: 1,
                        outlineColor: "rgba(0, 0, 0, .65)",
                        radius: 5,
                        buyColor: "#34d399",
                        sellColor: "#fb7185",
                        showLabels: false,
                    },
                },
            ]}],
        };
        const chart = new NightVision(containerId, {
            id: containerId,
            data: chartData,
            autoResize: true,
            timezone: "UTC",
            showLogo: false,
            colors: {
                back: "#020617",
                grid: "#1e293b",
                text: "#94a3b8",
                candleDw: "#fb7185",
                candleUp: "#34d399",
            },
            config: {
                DEFAULT_LEN: Math.max(candles.length, 30),
                MIN_ZOOM: Math.max(candles.length, 5),
                GRIDX: 60,
            },
        });
        chartRef.current = chart;
        return () => {
            if (chartRef.current === chart) chartRef.current = null;
            const container = document.getElementById(containerId);
            if (container) container.innerHTML = "";
        };
    }, [candles, containerId, lines, normalized, signature, trade.symbol]);

    if (!candles.length) return <div className="empty trade-chart-empty">No candle data for this trade.</div>;
    return (
        <div className="trade-candle-chart" role="img" aria-label={`${trade.symbol} candlestick execution chart`}>
            <div id={containerId} className="trade-candle-chart-host"/>
        </div>
    );
}
